import { DOCUMENT, Location } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  Renderer2,
} from '@angular/core';
import { ActivatedRoute, Data } from '@angular/router';
import { ApolloQueryResult } from '@apollo/client/core';
import { I18NextModule } from 'angular-i18next';
import { BaseComponent } from 'src/app/_shared/components/base/base.component';
import { METADATA_SERVICE, MetadataService } from 'src/app/common/metadata/metadata.service';
import { SafePipe } from 'src/app/_shared/pipes/safe.pipe';
import { Key } from 'src/app/common/utils/helpers';
import { FooterComponent } from '../../_shared/components/footer/footer.component';
import { HeaderComponent } from '../../_shared/components/header/header.component';
import { LoadingComponent } from '../../_shared/components/loading/loading.component';
import {
  GetPrivacyPolicyGQL,
  GetPrivacyPolicyQuery,
  GetSellingConditionsGQL,
  GetSellingConditionsQuery,
  GetTermsAndConditionsGQL,
  GetTermsAndConditionsQuery,
} from './terms-and-privacy.query.generated';

@Component({
  selector: 'sharry-terms-and-privacy',
  standalone: true,
  templateUrl: './terms-and-privacy.component.html',
  styleUrls: ['./terms-and-privacy.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [I18NextModule, HeaderComponent, FooterComponent, SafePipe, LoadingComponent],
})
export class TermsAndPrivacyComponent extends BaseComponent implements OnInit, OnDestroy {
  mode: 'privacy' | 'terms' | 'selling-conditions' | undefined;
  text: string | undefined;
  version: string | undefined;
  busy = false;

  constructor(
    private activatedRoute: ActivatedRoute,
    private renderer: Renderer2,
    private getTermsAndConditionsGQL: GetTermsAndConditionsGQL,
    private getPrivacyPolicyGQL: GetPrivacyPolicyGQL,
    private getSellingConditionsGQL: GetSellingConditionsGQL,
    private cd: ChangeDetectorRef,
    @Inject(DOCUMENT) private document: Document,
    @Inject(METADATA_SERVICE) private metadataService: MetadataService,
    private location: Location,
  ) {
    super();
  }

  @HostListener('document:click', ['$event']) onClick(event: Event) {
    const target: HTMLElement = event.target as HTMLElement;
    const fragment = target?.getAttribute('data-fragment');
    if (!fragment) return;
    this.document.querySelector(`#${fragment}`).scrollIntoView({ behavior: 'smooth' });
  }

  ngOnInit() {
    this.renderer.addClass(this.document.querySelector('html'), 'shrink');
    this.busy = true;
    this.cd.detectChanges();
    this.mode = this.activatedRoute.snapshot.data['mode' as Key<Data>];
    this.metadataService.setMetadataPage(this.mode, this.location.path());
    if (this.mode === 'privacy') {
      this.subscriptions.push(
        this.getPrivacyPolicyGQL.fetch().subscribe({
          next: res => {
            this.onResults(res);
          },
        }),
      );
    }
    if (this.mode === 'terms') {
      this.subscriptions.push(
        this.getTermsAndConditionsGQL.fetch().subscribe({
          next: res => {
            this.onResults(res);
          },
        }),
      );
    }
    if (this.mode === 'selling-conditions') {
      this.subscriptions.push(
        this.getSellingConditionsGQL.fetch().subscribe({
          next: res => {
            this.onResults(res);
          },
        }),
      );
    }
  }

  ngOnDestroy() {
    this.renderer.removeClass(this.document.querySelector('html'), 'shrink');
  }

  private onResults(
    results:
      | ApolloQueryResult<GetTermsAndConditionsQuery>
      | ApolloQueryResult<GetPrivacyPolicyQuery>
      | ApolloQueryResult<GetSellingConditionsQuery>,
  ) {
    this.text = results.data.results?.text;
    this.version = results.data.results?.version;
    this.busy = false;
    this.cd.detectChanges();
  }
}
